// 🔑 ID Alias Service - SSOT ID 변환 중앙 서비스
// [배경] modelId/priceId의 레거시 형식과 canonical 형식 간 변환을 중앙에서 관리
// [목적] 5단계 폴백 매칭을 2단계로 단순화하고, ID 일관성 보장

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  query, 
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  extractPureModelId,
  isCanonicalModelId,
  normalizeConnectivity,
  normalizeStorage,
  getConnectivityForPriceId,
  DeviceType,
  DeviceBrand
} from '../types/deviceCatalog';

// 🔑 Alias 문서 인터페이스
export interface IdAliasDocument {
  legacyId: string;      // 오염된 ID (예: samsung-tablet-...-256gb-wifi-139)
  canonicalId: string;   // 정상 ID (예: samsung-tablet-...)
  type: 'model' | 'price';
  createdAt: Timestamp;
  migratedAt?: Timestamp;
}

// 🔑 모델 입력 인터페이스
export interface ModelInput {
  brand: string;
  deviceType: string;
  series: string;
  modelEn: string;
}

// 🔑 Variant 입력 인터페이스
export interface VariantInput {
  storage: string;
  connectivity?: string;
}

/**
 * 🔑 ID Alias Service
 * 
 * SSOT 원칙:
 * - 모든 ID 생성/변환은 이 서비스를 통해서만 수행
 * - 읽기: 레거시 ID도 인식 (하위호환)
 * - 쓰기: 무조건 canonical ID만 사용
 */
export class IDAliasService {
  private static readonly ALIAS_COLLECTION = 'id_aliases';
  
  // 메모리 캐시 (성능 최적화)
  private static aliasCache = new Map<string, string>();
  private static reverseCache = new Map<string, string[]>();
  private static cacheInitialized = false;
  
  // ========================================
  // 🔑 Canonical ID 생성 (SSOT)
  // ========================================
  
  /**
   * 🔑 canonical modelId 생성
   * - storage, connectivity, 순번 절대 포함 금지
   * - 형식: {brand}-{deviceType}-{series}-{modelEn}
   */
  static createCanonicalModelId(model: ModelInput): string {
    return generateModelId({
      brand: model.brand,
      deviceType: model.deviceType,
      series: model.series,
      modelEn: model.modelEn
    });
  }
  
  /**
   * 🔑 canonical priceId 생성
   * - 형식: {modelId}-{storage}-{connectivity?}-{region}
   * - connectivity: Wi-Fi → 생략, Cellular → 'cellular'
   */
  static createCanonicalPriceId(
    modelId: string, 
    variant: VariantInput,
    region: 'KR' = 'KR'
  ): string {
    // modelId가 오염되어 있으면 정제
    const pureModelId = isCanonicalModelId(modelId) ? modelId : extractPureModelId(modelId);
    
    return generatePriceId(
      pureModelId,
      {
        storage: variant.storage,
        connectivity: getConnectivityForPriceId(variant.connectivity)
      },
      region
    );
  }
  
  // ========================================
  // 🔑 ID 해석 (레거시 → Canonical)
  // ========================================
  
  /**
   * 🔑 어떤 형태의 modelId든 canonical로 변환
   * 1. 이미 canonical이면 그대로 반환
   * 2. alias 테이블에서 찾기
   * 3. 패턴 매칭으로 정제 시도
   */
  static async resolveModelId(anyId: string): Promise<string> {
    // 1단계: 이미 canonical이면 그대로 반환
    if (isCanonicalModelId(anyId)) {
      return anyId;
    }
    
    // 2단계: 캐시 확인
    if (this.aliasCache.has(anyId)) {
      return this.aliasCache.get(anyId)!;
    }
    
    // 3단계: Firestore alias 테이블 확인
    try {
      const aliasDoc = await getDoc(doc(db, this.ALIAS_COLLECTION, anyId));
      if (aliasDoc.exists()) {
        const data = aliasDoc.data() as IdAliasDocument;
        this.aliasCache.set(anyId, data.canonicalId);
        return data.canonicalId;
      }
    } catch (error) {
      console.warn('⚠️ Alias 조회 실패:', error);
    }
    
    // 4단계: 패턴 매칭으로 정제
    const pureId = extractPureModelId(anyId);
    if (pureId !== anyId) {
      // 정제된 ID를 캐시에 저장
      this.aliasCache.set(anyId, pureId);
      return pureId;
    }
    
    // 정제 불가능하면 원본 반환
    return anyId;
  }
  
  /**
   * 🔑 어떤 형태의 priceId든 canonical로 변환
   */
  static async resolvePriceId(anyId: string): Promise<string> {
    // 캐시 확인
    if (this.aliasCache.has(anyId)) {
      return this.aliasCache.get(anyId)!;
    }
    
    // Firestore alias 테이블 확인
    try {
      const aliasDoc = await getDoc(doc(db, this.ALIAS_COLLECTION, anyId));
      if (aliasDoc.exists()) {
        const data = aliasDoc.data() as IdAliasDocument;
        this.aliasCache.set(anyId, data.canonicalId);
        return data.canonicalId;
      }
    } catch (error) {
      console.warn('⚠️ Price Alias 조회 실패:', error);
    }
    
    // priceId 정제 시도 (modelId 부분만 정제)
    // 형식: {modelId}-{storage}-{connectivity?}-{region}
    const parts = anyId.split('-');
    if (parts.length >= 2) {
      const region = parts[parts.length - 1]; // 마지막은 region (kr)
      
      // storage 패턴 찾기
      let storageIndex = -1;
      for (let i = parts.length - 2; i >= 0; i--) {
        if (/^\d+(gb|tb)$/i.test(parts[i])) {
          storageIndex = i;
          break;
        }
      }
      
      if (storageIndex > 0) {
        const modelIdParts = parts.slice(0, storageIndex);
        const pureModelId = extractPureModelId(modelIdParts.join('-'));
        const variantParts = parts.slice(storageIndex, parts.length - 1);
        const canonicalPriceId = [pureModelId, ...variantParts, region].join('-');
        
        if (canonicalPriceId !== anyId) {
          this.aliasCache.set(anyId, canonicalPriceId);
          return canonicalPriceId;
        }
      }
    }
    
    return anyId;
  }
  
  // ========================================
  // 🔑 검증 함수
  // ========================================
  
  /**
   * 🔑 modelId가 canonical 형식인지 검증
   */
  static isCanonicalModelId(modelId: string): boolean {
    return isCanonicalModelId(modelId);
  }
  
  /**
   * 🔑 priceId가 canonical 형식인지 검증
   * - modelId 부분이 canonical인지 확인
   */
  static isCanonicalPriceId(priceId: string): boolean {
    // priceId에서 modelId 부분 추출
    const parts = priceId.split('-');
    if (parts.length < 3) return false;
    
    // region (kr) 제거
    const withoutRegion = parts.slice(0, -1);
    
    // storage 패턴 찾기
    let storageIndex = -1;
    for (let i = withoutRegion.length - 1; i >= 0; i--) {
      if (/^\d+(gb|tb)$/i.test(withoutRegion[i])) {
        storageIndex = i;
        break;
      }
    }
    
    // 웨어러블인 경우 storage가 없을 수 있음
    if (storageIndex === -1) {
      // connectivity 패턴 찾기
      for (let i = withoutRegion.length - 1; i >= 0; i--) {
        if (/^(wifi|cellular)$/i.test(withoutRegion[i])) {
          storageIndex = i;
          break;
        }
      }
    }
    
    // modelId 부분 추출
    const modelIdParts = storageIndex > 0 ? withoutRegion.slice(0, storageIndex) : withoutRegion;
    const modelId = modelIdParts.join('-');
    
    return isCanonicalModelId(modelId);
  }
  
  // ========================================
  // 🔑 Alias 관리
  // ========================================
  
  /**
   * 🔑 레거시 ID → canonical ID 매핑 등록
   */
  static async registerAlias(
    legacyId: string, 
    canonicalId: string,
    type: 'model' | 'price'
  ): Promise<void> {
    try {
      const aliasDoc: IdAliasDocument = {
        legacyId,
        canonicalId,
        type,
        createdAt: Timestamp.now()
      };
      
      await setDoc(doc(db, this.ALIAS_COLLECTION, legacyId), aliasDoc);
      
      // 캐시 업데이트
      this.aliasCache.set(legacyId, canonicalId);
      
      // 역방향 캐시 업데이트
      const existing = this.reverseCache.get(canonicalId) || [];
      if (!existing.includes(legacyId)) {
        existing.push(legacyId);
        this.reverseCache.set(canonicalId, existing);
      }
      
      console.log(`✅ Alias 등록: ${legacyId} → ${canonicalId}`);
    } catch (error) {
      console.error('❌ Alias 등록 실패:', error);
      throw error;
    }
  }
  
  /**
   * 🔑 canonical ID의 모든 alias 조회
   */
  static async getAliases(canonicalId: string): Promise<string[]> {
    // 캐시 확인
    if (this.reverseCache.has(canonicalId)) {
      return this.reverseCache.get(canonicalId)!;
    }
    
    try {
      const q = query(
        collection(db, this.ALIAS_COLLECTION),
        where('canonicalId', '==', canonicalId)
      );
      
      const snapshot = await getDocs(q);
      const aliases = snapshot.docs.map(doc => doc.data().legacyId as string);
      
      this.reverseCache.set(canonicalId, aliases);
      return aliases;
    } catch (error) {
      console.error('❌ Alias 조회 실패:', error);
      return [];
    }
  }
  
  // ========================================
  // 🔑 캐시 관리
  // ========================================
  
  /**
   * 🔑 전체 alias 캐시 초기화 (앱 시작 시 호출)
   */
  static async initializeCache(): Promise<void> {
    if (this.cacheInitialized) return;
    
    try {
      const snapshot = await getDocs(collection(db, this.ALIAS_COLLECTION));
      
      snapshot.docs.forEach(doc => {
        const data = doc.data() as IdAliasDocument;
        this.aliasCache.set(data.legacyId, data.canonicalId);
        
        const existing = this.reverseCache.get(data.canonicalId) || [];
        if (!existing.includes(data.legacyId)) {
          existing.push(data.legacyId);
          this.reverseCache.set(data.canonicalId, existing);
        }
      });
      
      this.cacheInitialized = true;
      console.log(`✅ ID Alias 캐시 초기화 완료: ${this.aliasCache.size}개`);
    } catch (error) {
      console.error('❌ ID Alias 캐시 초기화 실패:', error);
    }
  }
  
  /**
   * 🔑 캐시 클리어
   */
  static clearCache(): void {
    this.aliasCache.clear();
    this.reverseCache.clear();
    this.cacheInitialized = false;
    console.log('✅ ID Alias 캐시 클리어 완료');
  }
  
  // ========================================
  // 🔑 정합성 검증 유틸리티
  // ========================================
  
  /**
   * 🔑 비정상 modelId 탐지 (storage/순번 포함)
   */
  static findNonCanonicalModelIds(modelIds: string[]): Array<{
    id: string;
    suggestedCanonical: string;
    issues: string[];
  }> {
    return modelIds
      .filter(id => !isCanonicalModelId(id))
      .map(id => {
        const issues: string[] = [];
        
        if (/-\d+(gb|tb)-/i.test(id)) issues.push('storage 포함');
        if (/-\d+$/.test(id) && !/\d+(gb|tb|inch|mm)$/i.test(id)) issues.push('순번 포함');
        if (/-wifi-|-cellular-/i.test(id)) issues.push('connectivity 포함');
        
        return {
          id,
          suggestedCanonical: extractPureModelId(id),
          issues
        };
      });
  }
  
  /**
   * 🔑 고아 alias 탐지 (canonical ID가 존재하지 않는 alias)
   */
  static async findOrphanedAliases(validCanonicalIds: Set<string>): Promise<string[]> {
    const orphaned: string[] = [];
    
    for (const [legacyId, canonicalId] of this.aliasCache.entries()) {
      if (!validCanonicalIds.has(canonicalId)) {
        orphaned.push(legacyId);
      }
    }
    
    return orphaned;
  }
  
  /**
   * 🔑 누락 alias 탐지 (등록이 필요한 레거시 ID)
   */
  static async findMissingAliases(
    knownLegacyIds: string[]
  ): Promise<string[]> {
    await this.initializeCache();
    
    return knownLegacyIds.filter(id => 
      !isCanonicalModelId(id) && !this.aliasCache.has(id)
    );
  }
}

export default IDAliasService;







